import { format, toZonedTime } from 'date-fns-tz'

import { timestampToDay } from '../../../helpers/helpers'

export const isDisabledAmongRange = (min, max, slotsDisabled) =>
  slotsDisabled && !!slotsDisabled.find((x) => x >= min && x <= max)

export const getReservedTimeRanges = (min, max, slotsDisabled) =>
  slotsDisabled && slotsDisabled.filter((x) => x >= min && x <= max)

export const selectTimeRange = ({ selectedTime, timeSlot, slotsDisabled }) => {
  if (selectedTime && selectedTime.startTime !== undefined && !selectedTime.endTime) {
    if (timeSlot > selectedTime.startTime) {
      return isDisabledAmongRange(selectedTime.startTime, timeSlot, slotsDisabled)
        ? selectTimeRange({ timeSlot })
        : { startTime: selectedTime.startTime, endTime: timeSlot }
    }
  } else if (selectedTime && selectedTime.startTime !== undefined && selectedTime.endTime) {
    if (timeSlot < selectedTime.startTime) {
      return isDisabledAmongRange(timeSlot, selectedTime.endTime, slotsDisabled)
        ? selectTimeRange({ timeSlot })
        : { startTime: timeSlot, endTime: selectedTime.endTime }
    } else if (timeSlot > selectedTime.endTime) {
      return isDisabledAmongRange(selectedTime.startTime, timeSlot, slotsDisabled)
        ? selectTimeRange({ timeSlot })
        : { startTime: selectedTime.startTime, endTime: timeSlot }
    }
  }

  return { startTime: timeSlot }
}

export const getTimezonedDate = (timestamp, timezone) => {
  const zoned = toZonedTime(new Date(timestamp), timezone)
  return new Date(new Date(zoned).setHours(0, 0, 0, 0))
}

export const getPricePerDay = (date, multidaySlots, price, timezone) => {
  const dayOfWeek = timestampToDay(new Date(new Date(date).setHours(0, 0, 0, 0)).getTime())
  const dateTimestamp = new Date(new Date(date).setHours(0, 0, 0, 0)).getTime()

  const singleSpecial = multidaySlots.find(
    (slot) =>
      getTimezonedDate(slot.startDate, timezone).getTime() === dateTimestamp && !slot.repeats,
  )

  const multiSpecial = multidaySlots.find((slot) => {
    const day = timestampToDay(getTimezonedDate(slot.startDate, timezone).getTime())
    return day === dayOfWeek && slot.repeats
  })
  if (singleSpecial) return singleSpecial.price
  if (multiSpecial) return multiSpecial.price
  return price
}

export const getReservedOnDateRange = (
  startDate,
  endDate,
  amenityReservations,
  timezone,
  ignoreStartEndDates = false,
) => {
  const filteredReservations = amenityReservations.filter((reservation) => {
    const timeZonedStartDate = getTimezonedDate(reservation.date, timezone).getTime()
    const timeZonedEndDate = getTimezonedDate(reservation.endDate, timezone).getTime()

    const isMatchingRange =
      (startDate <= timeZonedStartDate || startDate <= timeZonedEndDate) &&
      (endDate >= timeZonedStartDate || endDate >= timeZonedEndDate)

    if (ignoreStartEndDates && isMatchingRange) {
      const matchesEndStart =
        new Date(startDate).getTime() === timeZonedEndDate ||
        new Date(endDate).getTime() === timeZonedStartDate
      if (matchesEndStart) return false
    }

    return isMatchingRange
  })
  return filteredReservations
}

export const isDayAlreadyReserved = (
  dateString,
  amenityReservations,
  actualTzTimestamp,
  timezone,
) => {
  const reservedAmenity = amenityReservations
    .map((reservation) => {
      getTimezonedDate(reservation.date, timezone)
      if (
        getTimezonedDate(reservation.date, timezone) <= new Date(dateString) &&
        reservation.endDate &&
        getTimezonedDate(reservation.endDate, timezone) >= new Date(dateString) &&
        new Date(dateString) >= new Date(new Date(actualTzTimestamp).toDateString())
      ) {
        return reservation
      } else {
        return null
      }
    })
    .filter((n) => n)

  if (reservedAmenity.length > 0 && reservedAmenity[0].endDate) {
    if (reservedAmenity.length > 1) return 'fullDay'
    if (
      getTimezonedDate(reservedAmenity[0].date, timezone).toDateString() ===
      new Date(dateString).toDateString()
    ) {
      return 'startDay'
    }
    if (
      getTimezonedDate(reservedAmenity[0].endDate, timezone).toDateString() ===
      new Date(dateString).toDateString()
    ) {
      return 'endDay'
    }
    return 'fullDay'
  } else {
    return null
  }
}

export const getPricesFromTimestamps = (date, multidaySlots, price, timezone) => {
  const dayOfWeek = timestampToDay(new Date(new Date(date).setHours(0, 0, 0, 0)).getTime())
  const dateTimestamp = new Date(new Date(date).setHours(0, 0, 0, 0)).getTime()

  const singleSpecial = multidaySlots.find(
    (slot) =>
      getTimezonedDate(slot.startDate, timezone).getTime() === dateTimestamp && !slot.repeats,
  )
  const multiSpecial = multidaySlots.find(
    (slot) =>
      timestampToDay(getTimezonedDate(slot.startDate, timezone).getTime()) === dayOfWeek &&
      slot.repeats,
  )

  if (singleSpecial) {
    return {
      price: Number(singleSpecial.price),
      date: date.getTime(),
      isSpecialPrice: true,
    }
  }

  if (multiSpecial) {
    return {
      price: Number(multiSpecial.price),
      date: date.getTime(),
      isSpecialPrice: true,
    }
  }
  return {
    price,
    date: date.getTime(),
    isSpecialPrice: false,
  }
}

export const getFinalPrice = (reservingDates, extraFee) => {
  const datesPrice =
    reservingDates && reservingDates.length
      ? reservingDates.reduce((a, b) => a + b.price, 0) -
        (reservingDates.length > 1 ? reservingDates[reservingDates.length - 1].price : 0)
      : 0
  return extraFee && extraFee > 0 ? datesPrice + extraFee : datesPrice
}

export const getDateAndPriceForSpecialDates = (reservingDates) => {
  const cuttedReservingDates =
    reservingDates.length > 1 ? reservingDates.slice(0, -1) : reservingDates
  const prices = cuttedReservingDates.map((date) => {
    if (date.isSpecialPrice) return `${format(date.date, 'MM/dd/yyyy')} - $${date.price}`
    return null
  })
  return prices.filter((n) => n).join('; ')
}

// Utility function to get booked locations during a specific time period
export const getBookedLocationsDuringPeriod = (
  reservations,
  startTime,
  endTime,
  dayPosition = 'middle',
) => {
  const bookedLocations = new Set()
  const checkDate = new Date(startTime).setHours(0, 0, 0, 0)

  reservations.forEach((reservation) => {
    const reservationStartDate = new Date(reservation.date).setHours(0, 0, 0, 0)
    const reservationEndDate = new Date(reservation.endDate).setHours(0, 0, 0, 0)

    if (dayPosition === 'first' && reservationEndDate === checkDate) {
      return
    }

    if (dayPosition === 'last' && reservationStartDate === checkDate) {
      return
    }

    // Check if reservation overlaps with the given period
    if (
      reservationStartDate <= startTime &&
      new Date(reservation.endDate).setHours(23, 59, 59, 999) >= endTime
    ) {
      const locationId = reservation.amenity?.locationId
      if (locationId) {
        bookedLocations.add(locationId)
      }
    }
  })

  return bookedLocations
}

// Utility function to get available locations during a time period
export const getAvailableLocationsDuringPeriod = (
  reservations,
  allLocationIds,
  startTime,
  endTime,
  dayPosition,
) => {
  const bookedLocations = getBookedLocationsDuringPeriod(
    reservations,
    startTime,
    endTime,
    dayPosition,
  )
  return allLocationIds.filter((locationId) => !bookedLocations.has(locationId))
}

// Calculate available locationIds for the selected date range
export const calculateAvailableLocationIds = (selectedDays, locations, amenityReservations) => {
  if (!locations || locations.length === 0) {
    return []
  }

  // Start with all available location IDs
  const allLocationIds = locations.map((location) => location.id)
  let availableIds = [...allLocationIds]

  // For each day in the selected range, find which locations are still available
  selectedDays.forEach((day, index) => {
    const dayStart = new Date(day).setHours(0, 0, 0, 0)
    const dayEnd = new Date(day).setHours(23, 59, 59, 999)

    // Determine position of day in the new booking range
    const isFirstDay = index === 0
    const isLastDay = index === selectedDays.length - 1
    const dayPosition = isFirstDay ? 'first' : isLastDay ? 'last' : 'middle'

    // Get available locations for this specific day
    const dayAvailableIds = getAvailableLocationsDuringPeriod(
      amenityReservations,
      allLocationIds,
      dayStart,
      dayEnd,
      dayPosition,
    )

    // Keep only locations that are available on this day AND all previous days
    availableIds = availableIds.filter((id) => dayAvailableIds.includes(id))
  })

  return availableIds
}
